import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const KINDS = ["okta", "azure_ad", "google_workspace", "generic"];
const ROLES = ["analyst", "admin"];
const empty = {
  org_id: "", slug: "", name: "", kind: "okta",
  issuer: "", client_id: "", client_secret: "",
  scopes: ["openid", "email", "profile"], redirect_uri: "", default_role: "analyst",
};
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/;

export default function SSO() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/sso/providers"); setItems(data); }
    catch (err) { console.error("sso load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!SLUG_RE.test(form.slug)) {
      toast.error("Slug must be 2–63 chars, lowercase a–z, 0–9, hyphens; start/end alphanumeric.");
      return;
    }
    try { await api.post("/sso/providers", form); toast.success("Provider created"); setOpen(false); setForm(empty); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this SSO provider?")) return;
    try { await api.delete(`/sso/providers/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout title="SSO Providers" subtitle="OIDC IDENTITY">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="sso-count">
          {items.length} PROVIDERS
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-sso-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Plus className="w-4 h-4" /> NEW PROVIDER</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><KeyRound className="w-4 h-4 text-cyan-400" /> Create OIDC Provider</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div><L>Org id</L><Input data-testid="sso-org-input" value={form.org_id} onChange={(e) => setForm({ ...form, org_id: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Display name</L><Input data-testid="sso-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
              <div className="col-span-2"><L>URL slug (used in /sso/oidc/&lt;slug&gt;/callback)</L><Input data-testid="sso-slug-input" placeholder="google" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Kind</L>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="sso-kind-select" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {KINDS.map((k) => <SelectItem key={k} value={k} className="font-mono uppercase text-xs">{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><L>Default role</L>
                <Select value={form.default_role} onValueChange={(v) => setForm({ ...form, default_role: v })}>
                  <SelectTrigger data-testid="sso-role-select" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {ROLES.map((r) => <SelectItem key={r} value={r} className="font-mono uppercase text-xs">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><L>Issuer URL</L><Input data-testid="sso-issuer-input" placeholder="https://acme.okta.com" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Client id</L><Input data-testid="sso-clientid-input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Client secret</L><Input data-testid="sso-secret-input" type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div className="col-span-2"><L>Redirect URI</L><Input data-testid="sso-redirect-input" placeholder="https://api.example.com/api/sso/oidc/<slug>/callback" value={form.redirect_uri} onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div className="col-span-2"><L>Scopes (comma-separated)</L><Input data-testid="sso-scopes-input" value={form.scopes.join(",")} onChange={(e) => setForm({ ...form, scopes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-sso-btn" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CREATE</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="sso-list">
        {items.map((p) => (
          <div key={p.provider_id} data-testid={`sso-card-${p.provider_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display text-base font-bold tracking-tight">{p.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{p.slug || p.provider_id} · org {p.org_id}</div>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-cyan-500/40 text-cyan-400 bg-cyan-500/10 rounded-sm">{p.kind}</span>
            </div>
            <div className="font-mono text-[11px] text-zinc-400 break-all">{p.issuer}</div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
              <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">default → {p.default_role}</span>
              {user?.role === "admin" && (
                <Button size="sm" variant="ghost" data-testid={`delete-sso-${p.provider_id}`} onClick={() => del(p.provider_id)} className="gap-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no SSO providers configured.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
